/* eslint-disable react/jsx-no-useless-fragment */
import { useEffect, useState } from 'react';

import MUIAvatar, { AvatarProps as MUIAvatarProps } from '@mui/material/Avatar';
import MUIBadge from '@mui/material/Badge';
import classNames from 'classnames';

import { Box } from '../Box';
import {
  useBlobUrl,
  useIntersectionObserver,
  type UseIntersectionObserverOptions,
} from '../hooks';

import { useStyles } from './Avatar.styles';

/** Возможные размеры аватара. */
export type AvatarSize =
  | 'extraLarge'
  | 'large'
  | 'medium'
  | 'small'
  | 'extraSmall';

/** Свойства компонента {@link Avatar}. */
type AvatarProps = MUIAvatarProps & {
  /**
   * Форма аватара.
   *
   * @defaultValue `'circle'`
   */
  variant?: 'circle' | 'square';

  /**
   * Определяет, будет ли отображаться значок на аватаре.
   *
   * @defaultValue `false`
   */
  withBadge?: boolean;

  /**
   * Размер аватара.
   *
   * @defaultValue `'medium'`
   */
  size?: AvatarSize;

  /** Функция, возвращающая промис, который разрешается в Blob объект, содержащий изображение аватара. */
  loadPhoto?: () => Promise<Blob>;
  intersectionObserverProps?: UseIntersectionObserverOptions;
};

/**
 * Настраиваемый компонент аватара, построенный с использованием Material-UI.
 *
 * @internal
 * @param variant - Форма аватара.
 * @param withBadge - Определяет, будет ли отображаться значок на аватаре.
 * @param size - Размер аватара.
 * @param loadPhoto - Функция, возвращающая промис, который разрешается в Blob объект, содержащий изображение аватара.
 * @param intersectionObserverProps - Настройки для IntersectionObserver, используемого для ленивой загрузки изображения.
 * @param children - Содержимое, отображаемое внутри аватара.
 * @param alt - Альтернативный текст для изображения аватара.
 * @param src - URL-адрес изображения аватара.
 * @returns Компонент Avatar.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link AvatarProps}
 */
export const Avatar = (props: AvatarProps) => {
  const {
    variant = 'circle',
    withBadge,
    size = 'medium',
    children,
    loadPhoto,
    alt,
    src: url,
    intersectionObserverProps,
  } = props;

  const { classes } = useStyles();
  const { createUrl, revokeUrl } = useBlobUrl();
  const [isInView, setIsInView] = useState(false);
  const [ref, entry] = useIntersectionObserver({
    rootMargin: '100px',
    ...intersectionObserverProps,
  });

  useEffect(() => {
    if (entry?.isIntersecting) {
      setIsInView(true);
    }
  }, [entry?.isIntersecting]);

  const [src, setSrc] = useState(url);

  useEffect(() => {
    setSrc(url);

    if (url) {
      revokeUrl();
    }
  }, [url, revokeUrl]);

  useEffect(() => {
    let isCancelled = false;

    if (!src && isInView) {
      loadPhoto?.()
        .then((blob) => {
          if (!isCancelled) {
            setSrc(createUrl(blob));
          }
        })
        .catch(() => {
          // do nothing
        });
    }

    return () => {
      isCancelled = true;
    };
  }, [loadPhoto, src, isInView, createUrl]);

  return (
    <Box
      ref={ref}
      component="span"
      className={classes.wrapper}
      display="inline-flex"
      flexShrink={0}
      position="relative"
    >
      {withBadge ? (
        <MUIBadge
          className={`aqa_avatar_badge ${classes.badge}`}
          overlap="circular"
          variant="dot"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Avatar
            className={classNames(
              'aqa_avatar',
              variant === 'square' && classes.avatarSquare,
            )}
            alt={alt}
            children={children}
            src={src}
            size={size}
          />
        </MUIBadge>
      ) : (
        <MUIAvatar
          src={src}
          alt={alt}
          children={src ? <></> : children}
          data-testid="avatar"
          className={classNames(
            'aqa_avatar',
            classes.avatar,
            classes[size],
            variant === 'square' && classes.avatarSquare,
          )}
        />
      )}
    </Box>
  );
};
